import React, { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import { API_BASE_URL } from '../App';
import { validateEmail } from '../lib/emailValidation';
import {
  buildTwitterShareUrl,
  buildFacebookShareUrl,
  buildSmsShareUrl,
  copyToClipboard,
} from '../lib/share';

function SmsIcon() {
  return (
    <svg className="invite-friends__social-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"
      />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="invite-friends__social-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
      />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg className="invite-friends__social-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      />
    </svg>
  );
}

function InviteFriendsContent({ compact = false }) {
  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copyStatus, setCopyStatus] = useState(null);
  const [friendEmail, setFriendEmail] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [emailStatus, setEmailStatus] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const session = Cookies.get('session');
    if (!session) {
      setLoading(false);
      return;
    }
    fetch(`${API_BASE_URL}/invites/me`, {
      headers: { Authorization: session },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setInviteData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleCopy = useCallback(async () => {
    if (!inviteData?.inviteUrl) return;
    const ok = await copyToClipboard(inviteData.inviteUrl);
    setCopyStatus(ok ? { type: 'ok', message: 'Link copied!' } : { type: 'error', message: 'Could not copy' });
    setTimeout(() => setCopyStatus(null), 2500);
  }, [inviteData]);

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setEmailStatus(null);
    const check = validateEmail(friendEmail);
    if (!check.ok) {
      setEmailStatus({ type: 'error', message: check.error });
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/invites/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: Cookies.get('session'),
        },
        body: JSON.stringify({
          toEmail: check.normalized,
          message: personalMessage.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setEmailStatus({ type: 'error', message: data.error || 'Could not send invite' });
        return;
      }
      setEmailStatus({ type: 'ok', message: data.message || 'Invite sent!' });
      setFriendEmail('');
      setPersonalMessage('');
    } catch (err) {
      setEmailStatus({ type: 'error', message: err.message || 'Network error' });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <p className="account-page__hint">Loading invite link…</p>;
  }

  if (!inviteData?.inviteUrl) {
    return <p className="account-page__hint">Sign in to invite friends.</p>;
  }

  const shareText = 'Join me on Plate Chase — the license plate spotting game!';
  const shareMessage = `${shareText} ${inviteData.inviteUrl}`;
  const twitterUrl = buildTwitterShareUrl(shareText, inviteData.inviteUrl);
  const facebookUrl = buildFacebookShareUrl(inviteData.inviteUrl);
  const smsUrl = buildSmsShareUrl(shareMessage);

  return (
    <div className={`invite-friends${compact ? ' invite-friends--compact' : ''}`}>
      <p className="account-page__hint">
        Share your link or send an email invite. Friends who sign up will be linked to your account.
      </p>

      <div className="invite-friends__link-row">
        <input
          type="text"
          className="invite-friends__link-input"
          value={inviteData.inviteUrl}
          readOnly
          aria-label="Invite link"
        />
        <button type="button" className="invite-friends__copy-btn" onClick={handleCopy}>
          Copy
        </button>
      </div>
      {copyStatus && (
        <p className={`invite-friends__status invite-friends__status--${copyStatus.type}`} role="status">
          {copyStatus.message}
        </p>
      )}

      <p className="invite-friends__share-label">Share via</p>
      <div className="invite-friends__share-row">
        <a
          href={smsUrl}
          className="invite-friends__social-btn invite-friends__social-btn--sms"
        >
          <SmsIcon />
          <span>Text message</span>
        </a>
        <a
          href={twitterUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="invite-friends__social-btn invite-friends__social-btn--x"
        >
          <XIcon />
          <span>X</span>
        </a>
        <a
          href={facebookUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="invite-friends__social-btn invite-friends__social-btn--facebook"
        >
          <FacebookIcon />
          <span>Facebook</span>
        </a>
      </div>

      <form className="invite-friends__email-form" onSubmit={handleSendEmail}>
        <label className="account-page__hint" htmlFor="invite-friend-email">
          Email invite
        </label>
        <input
          id="invite-friend-email"
          type="email"
          className="invite-friends__email-input"
          placeholder="friend@example.com"
          value={friendEmail}
          onChange={(e) => setFriendEmail(e.target.value)}
          disabled={sending}
        />
        <textarea
          className="invite-friends__message-input"
          placeholder="Optional personal message"
          value={personalMessage}
          onChange={(e) => setPersonalMessage(e.target.value)}
          rows={2}
          disabled={sending}
        />
        <button type="submit" className="invite-friends__send-btn" disabled={sending}>
          {sending ? 'Sending…' : 'Send invite email'}
        </button>
        {emailStatus && (
          <p
            className={`invite-friends__status invite-friends__status--${emailStatus.type}`}
            role={emailStatus.type === 'error' ? 'alert' : 'status'}
          >
            {emailStatus.message}
          </p>
        )}
      </form>
    </div>
  );
}

function InviteFriendsModal({ isOpen, onClose }) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="invite-modal-overlay" onClick={onClose}>
      <div
        className="invite-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-modal-title"
      >
        <div className="invite-modal__accent" aria-hidden="true" />
        <h2 id="invite-modal-title" className="invite-modal__title">
          Invite friends
        </h2>
        <InviteFriendsContent compact />
        <button type="button" className="invite-modal__close" onClick={onClose} aria-label="Close">
          Close
        </button>
      </div>
    </div>
  );
}

export { InviteFriendsContent };
export default InviteFriendsModal;
